/**
 * flip-slide-carousel.js
 * Combo slide + flip animation carousel.
 * Slides stack absolutely; transitions combine translateX + rotateY for a
 * perspective-flip-slide effect instead of plain scroll-snap.
 *
 * Usage:
 *   import { initFlipSlideCarousel } from '/js/utils/flip-slide-carousel.js';
 *
 *   const { goTo, step, pause, resume } = initFlipSlideCarousel({
 *     slides:   document.querySelectorAll('#my-track .my-slide'),
 *     dots:     document.querySelectorAll('#my-dots .my-dot'),
 *     interval: 5500,
 *     onSlide:  (idx) => {},
 *   });
 *
 * Required CSS on the container element:
 *   position: relative; height: 100dvh; overflow: hidden; perspective: 1200px;
 *
 * Required CSS on each slide element (added automatically if missing):
 *   position: absolute; inset: 0;
 */

const ANIM_DURATION = 650; // ms — must match CSS keyframe duration

export function initFlipSlideCarousel({
  slides: slideNodes,
  dots = [],
  bars = [],
  interval = 5500,
  prevBtn,
  nextBtn,
  onSlide,
} = {}) {
  const slides = Array.from(slideNodes || []);
  const dotList = Array.from(dots);
  const barList = Array.from(bars);
  if (!slides.length) return {};

  let cur = 0;
  let animating = false;
  let paused = false;
  let timer = null;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Initialise slide stacking
  slides.forEach((s, i) => {
    s.style.position = 'absolute';
    s.style.inset = '0';
    s.style.opacity = i === 0 ? '1' : '0';
    s.style.pointerEvents = i === 0 ? 'auto' : 'none';
    s.style.zIndex = i === 0 ? '1' : '0';
  });

  function syncDots(idx) {
    dotList.forEach((d, i) => d.classList.toggle('active', i === idx));
    barList.forEach((b, i) => b.classList.toggle('active', i === idx));
    if (typeof onSlide === 'function') onSlide(idx);
  }

  function goTo(next, forceDir) {
    next = ((next % slides.length) + slides.length) % slides.length;
    if (next === cur || animating) return;

    const dir = forceDir !== undefined ? forceDir : (next > cur ? 1 : -1);
    const outClass = dir > 0 ? 'fsc-out-left' : 'fsc-out-right';
    const inClass  = dir > 0 ? 'fsc-in-right' : 'fsc-in-left';

    const outSlide = slides[cur];
    const inSlide  = slides[next];

    animating = true;

    if (reduced) {
      // Skip animation for accessibility
      outSlide.style.opacity = '0';
      outSlide.style.pointerEvents = 'none';
      outSlide.style.zIndex = '0';
      inSlide.style.opacity = '1';
      inSlide.style.pointerEvents = 'auto';
      inSlide.style.zIndex = '1';
      cur = next;
      animating = false;
      syncDots(cur);
      if (!paused) resetTimer();
      return;
    }

    // Prep incoming slide (invisible, positioned for entry)
    inSlide.style.opacity = '0';
    inSlide.style.zIndex = '2';
    inSlide.style.pointerEvents = 'none';

    // Small rAF delay so browser registers the initial state before animating
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        outSlide.classList.add(outClass);
        inSlide.classList.add(inClass);
        inSlide.style.opacity = '1';

        setTimeout(() => {
          outSlide.classList.remove(outClass);
          inSlide.classList.remove(inClass);

          outSlide.style.opacity = '0';
          outSlide.style.pointerEvents = 'none';
          outSlide.style.zIndex = '0';

          inSlide.style.opacity = '1';
          inSlide.style.pointerEvents = 'auto';
          inSlide.style.zIndex = '1';

          cur = next;
          animating = false;
          syncDots(cur);
          if (!paused) resetTimer();
        }, ANIM_DURATION);
      });
    });
  }

  function step(dir) {
    const next = ((cur + dir) % slides.length + slides.length) % slides.length;
    goTo(next, dir);
  }

  function advance() { step(1); }

  function startTimer() {
    if (timer || paused || slides.length <= 1 || interval <= 0) return;
    timer = setInterval(advance, interval);
  }

  function stopTimer() {
    clearInterval(timer);
    timer = null;
  }

  function resetTimer() {
    stopTimer();
    startTimer();
  }

  function pause() {
    paused = true;
    stopTimer();
  }

  function resume() {
    paused = false;
    resetTimer();
  }

  // Wire nav buttons
  if (prevBtn) prevBtn.addEventListener('click', () => { step(-1); resetTimer(); });
  if (nextBtn) nextBtn.addEventListener('click', () => { step(1); resetTimer(); });

  // Wire dot/bar buttons so clicking a dot navigates to that slide
  dotList.forEach((d, i) => d.addEventListener('click', () => { goTo(i); resetTimer(); }));
  barList.forEach((b, i) => b.addEventListener('click', () => { goTo(i); resetTimer(); }));

  // Pause when tab/window hidden
  document.addEventListener('visibilitychange', () => {
    document.hidden ? stopTimer() : (!paused && startTimer());
  });

  // Pause when carousel scrolls off-screen
  const firstSlide = slides[0];
  if (firstSlide && 'IntersectionObserver' in window) {
    const container = firstSlide.parentElement;
    if (container) {
      new IntersectionObserver(
        ([entry]) => { entry.isIntersecting ? (!paused && startTimer()) : stopTimer(); },
        { threshold: 0.2 }
      ).observe(container);
    }
  }

  // Touch-swipe: horizontal swipe triggers slide change, vertical triggers content scroll
  const swipeContainer = slides[0]?.parentElement;
  if (swipeContainer) {
    let touchStartX = 0;
    let touchStartY = 0;
    swipeContainer.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    swipeContainer.addEventListener('touchend', e => {
      const dx = touchStartX - e.changedTouches[0].clientX;
      const dy = touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        step(dx > 0 ? 1 : -1);
      }
    }, { passive: true });
  }

  syncDots(0);
  startTimer();

  return { goTo, step, pause, resume, getCurrent: () => cur };
}
